/**
 * ComplianceDashboard - Main page for ECM monthly contact compliance
 * Replaces notification noise with a single actionable view showing all
 * members needing monthly contact, their status, and management actions.
 * Now includes row selection, bulk notification management, and per-member
 * notification preferences.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  RefreshCw,
  CheckCheck,
  Settings2,
  BellOff,
  Bell,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

import { ComplianceSummaryCards } from './ComplianceSummaryCards';
import { ComplianceMemberTable } from './ComplianceMemberTable';
import { SnoozeDialog } from './SnoozeDialog';
import { ActiveSnoozesPanel } from './ActiveSnoozesPanel';
import { NotificationPreferencesDialog } from './NotificationPreferencesDialog';
import { BulkNotificationPanel } from './BulkNotificationPanel';
import {
  useComplianceDashboard,
  useSnoozes,
  useBulkAcknowledge,
  useBulkNotificationPreferences,
} from '@/hooks/useEcmReminders';
import type { MemberContactStatus, ContactStatus } from '@/services/api/ecmReminders';

interface ComplianceDashboardProps {
  onNavigate?: (tab: string) => void;
}

export const ComplianceDashboard: React.FC<ComplianceDashboardProps> = ({ onNavigate }) => {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [snoozeTarget, setSnoozeTarget] = useState<MemberContactStatus | null>(null);
  const [notifPrefsTarget, setNotifPrefsTarget] = useState<MemberContactStatus | null>(null);
  const [bulkPanelOpen, setBulkPanelOpen] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);

  const {
    members,
    summary,
    total,
    page,
    pageSize,
    hasMore,
    loading,
    refetch: refetchDashboard,
    setPage,
    setFilters,
  } = useComplianceDashboard({
    include_snoozed: true,
    page_size: 50,
  });

  const { snoozes, createSnooze, cancelSnooze, refetch: refetchSnoozes } = useSnoozes();
  const { acknowledge, loading: ackLoading } = useBulkAcknowledge();
  const { bulkUpdate: bulkPause, updating: bulkPausing } = useBulkNotificationPreferences();

  // Compute paused/opted-out count from loaded members
  const pausedOrOptedOutCount = useMemo(() => {
    return members.filter(
      (m) => m.notifications_paused || m.notifications_opted_out
    ).length;
  }, [members]);

  // Handle status filter from summary cards
  const handleFilterClick = useCallback(
    (status: string | null) => {
      setStatusFilter(status);
      // The 'paused_or_opted_out' filter is handled client-side since it's not a backend status
      if (status === 'paused_or_opted_out') {
        setFilters({ contact_status: undefined });
      } else {
        setFilters({ contact_status: status as ContactStatus | undefined });
      }
    },
    [setFilters]
  );

  // Client-side filter for paused/opted-out
  const displayMembers = useMemo(() => {
    if (statusFilter === 'paused_or_opted_out') {
      return members.filter((m) => m.notifications_paused || m.notifications_opted_out);
    }
    return members;
  }, [members, statusFilter]);

  // Bulk acknowledge all ECM reminders
  const handleBulkAcknowledge = async () => {
    const result = await acknowledge({ reminder_type: 'follow_up_contact' });
    if (result) {
      toast.success(result.message);
      refetchDashboard();
    } else {
      toast.error('Failed to acknowledge reminders');
    }
  };

  // Navigate to progress note form for member
  const handleDocumentContact = (member: MemberContactStatus) => {
    if (onNavigate) {
      localStorage.setItem('selectedEnrollmentId', String(member.enrollment_id));
      localStorage.setItem('navigate_enrollment_step', 'ECM_PROGRESS_NOTES');
      onNavigate('enrollments-view');
    }
  };

  // Open snooze dialog
  const handleSnoozeMember = (member: MemberContactStatus) => {
    setSnoozeTarget(member);
  };

  // Navigate to enrollment detail
  const handleViewEnrollment = (member: MemberContactStatus) => {
    if (onNavigate) {
      localStorage.setItem('selectedEnrollmentId', String(member.enrollment_id));
      onNavigate('enrollments-view');
    }
  };

  // Cancel a snooze
  const handleCancelSnooze = async (snoozeId: number) => {
    const success = await cancelSnooze(snoozeId);
    if (success) {
      toast.success('Snooze cancelled. Reminders will resume.');
      refetchDashboard();
    } else {
      toast.error('Failed to cancel snooze');
    }
  };

  // After snooze created, refresh both
  const handleSnoozeCreated = () => {
    refetchDashboard();
    refetchSnoozes();
  };

  // Row selection
  const handleToggleSelect = useCallback((memberId: number) => {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    const visibleIds = displayMembers.map((m) => m.member_id);
    const allSelected = visibleIds.every((id) => selectedMemberIds.includes(id));
    if (allSelected) {
      setSelectedMemberIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedMemberIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  }, [displayMembers, selectedMemberIds]);

  // Quick bulk actions from floating bar
  const handleQuickPause = async () => {
    if (selectedMemberIds.length === 0) return;
    const result = await bulkPause({
      member_ids: selectedMemberIds,
      notification_paused: true,
      paused_reason: 'Bulk pause from dashboard',
    });
    if (result) {
      toast.success(result.message);
      setSelectedMemberIds([]);
      refetchDashboard();
    } else {
      toast.error('Bulk pause failed');
    }
  };

  const handleQuickResume = async () => {
    if (selectedMemberIds.length === 0) return;
    const result = await bulkPause({
      member_ids: selectedMemberIds,
      notification_paused: false,
    });
    if (result) {
      toast.success(result.message);
      setSelectedMemberIds([]);
      refetchDashboard();
    } else {
      toast.error('Bulk resume failed');
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <p className="text-[13px] text-ink-500">
          Monthly contact tracking &middot; {total} active enrollments
        </p>
        <div className="flex-1" />
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkPanelOpen(true)}
          >
            <Settings2 className="w-4 h-4 mr-1" />
            Notification Settings
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkAcknowledge}
            disabled={ackLoading}
          >
            <CheckCheck className="w-4 h-4 mr-1" />
            Acknowledge All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { refetchDashboard(); refetchSnoozes(); }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="mb-6">
          <ComplianceSummaryCards
            summary={summary}
            activeFilter={statusFilter}
            onFilterClick={handleFilterClick}
            pausedOrOptedOutCount={pausedOrOptedOutCount}
          />
        </div>
      )}

      {/* Floating selection bar */}
      {selectedMemberIds.length > 0 && (
        <div className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            {selectedMemberIds.length} selected
          </span>
          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={handleQuickPause}
              disabled={bulkPausing}
            >
              <BellOff className="w-3 h-3 mr-1" />
              Pause All
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-green-300 text-green-700 hover:bg-green-50"
              onClick={handleQuickResume}
              disabled={bulkPausing}
            >
              <Bell className="w-3 h-3 mr-1" />
              Resume All
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setBulkPanelOpen(true)}
            >
              <Settings2 className="w-3 h-3 mr-1" />
              Manage
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-gray-500"
              onClick={() => setSelectedMemberIds([])}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* Main Table */}
        <div className="flex-1 min-w-0">
          <ComplianceMemberTable
            members={displayMembers}
            total={statusFilter === 'paused_or_opted_out' ? displayMembers.length : total}
            page={page}
            pageSize={pageSize}
            hasMore={statusFilter === 'paused_or_opted_out' ? false : hasMore}
            loading={loading}
            onPageChange={setPage}
            onDocumentContact={handleDocumentContact}
            onSnoozeMember={handleSnoozeMember}
            onViewEnrollment={handleViewEnrollment}
            onManageNotifications={(member) => setNotifPrefsTarget(member)}
            selectedMemberIds={selectedMemberIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
          />
        </div>

        {/* Sidebar: Active Snoozes */}
        {snoozes.length > 0 && (
          <div className="w-72 flex-shrink-0">
            <ActiveSnoozesPanel
              snoozes={snoozes}
              loading={false}
              onCancelSnooze={handleCancelSnooze}
            />
          </div>
        )}
      </div>

      {/* Snooze Dialog */}
      {snoozeTarget && (
        <SnoozeDialog
          isOpen={!!snoozeTarget}
          onClose={() => setSnoozeTarget(null)}
          memberId={snoozeTarget.member_id}
          memberName={snoozeTarget.member_name}
          enrollmentId={snoozeTarget.enrollment_id}
          onSnoozeCreated={handleSnoozeCreated}
          createSnooze={createSnooze}
        />
      )}

      {/* Notification Preferences Dialog (per-member) */}
      {notifPrefsTarget && (
        <NotificationPreferencesDialog
          isOpen={!!notifPrefsTarget}
          onClose={() => setNotifPrefsTarget(null)}
          memberId={notifPrefsTarget.member_id}
          memberName={notifPrefsTarget.member_name}
          onPreferencesUpdated={() => {
            refetchDashboard();
          }}
        />
      )}

      {/* Bulk Notification Panel (sheet) */}
      <BulkNotificationPanel
        isOpen={bulkPanelOpen}
        onClose={() => setBulkPanelOpen(false)}
        selectedMemberIds={selectedMemberIds}
        allMembers={members}
        onUpdated={() => {
          setSelectedMemberIds([]);
          refetchDashboard();
        }}
      />
    </div>
  );
};
